import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";

import { getImage } from "../../shopping/Cart/CartItemFields";
import LocationAssetDetailsModal from "../../../components/TablePro/components/warehouse/LocationAssetDetailsModal";

function formatWeight(weight) {
  return `${Number(weight || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} lbs`;
}

function buildBreakdownRows(inventoryItems) {
  return inventoryItems.map(inventoryItem => {
    const quantity = Number(inventoryItem?.quantity || 0);
    const assetWeight = Number(inventoryItem?.asset?.weight || 0);
    const lineWeight = quantity * assetWeight;

    return {
      inventoryItem,
      quantity,
      assetWeight,
      lineWeight,
    };
  });
}

function weightsDiffer(a, b) {
  return Math.abs(Number(a || 0) - Number(b || 0)) >= 0.01;
}

export default function OutboundWeightBreakdownModal({
  open,
  onClose,
  inventoryItems = [],
  totalOutboundWeight = 0,
  calculatedTotalOutboundWeight = 0,
  manualOutboundWeight = "",
  onSaveManualOutboundWeight,
  overrideModalOpen = false,
  onOpenOverrideModal,
  onCloseOverrideModal,
  savingManualOutboundWeight = false,
  disabled = false,
}) {
  const rows = useMemo(() => buildBreakdownRows(inventoryItems), [inventoryItems]);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [draftManualOutboundWeight, setDraftManualOutboundWeight] = useState(manualOutboundWeight || "");

  useEffect(() => {
    if (overrideModalOpen) {
      setDraftManualOutboundWeight(manualOutboundWeight || "");
    }
  }, [overrideModalOpen, manualOutboundWeight]);

  const hasManualOverride =
    manualOutboundWeight != null && manualOutboundWeight !== "" && Number.isFinite(Number(manualOutboundWeight));
  const itemTotalDiffersFromBillingWeight = weightsDiffer(
    totalOutboundWeight,
    calculatedTotalOutboundWeight
  );

  const openOverrideModal = () => {
    if (disabled) return;
    onOpenOverrideModal?.();
  };

  const closeOverrideModal = () => {
    setDraftManualOutboundWeight(manualOutboundWeight || "");
    onCloseOverrideModal?.();
  };

  const saveOverride = () => {
    onSaveManualOutboundWeight?.(draftManualOutboundWeight);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          maxWidth: "760px",
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ pt: 3, pb: 1, position: "relative", textAlign: "center", color: "black" }}>
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Outbound Weight Breakdown
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 16, top: 16 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, px: 4 }}>
        <Box mb={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Total outbound weight is the current weight used for billing. The line-item table below
            shows each item's asset weight multiplied by its requested quantity.
          </Typography>
          <Box mt={1.5}>
            <Typography component="span" fontSize={16} fontWeight={500} color="textPrimary">
              Total:{" "}
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={openOverrideModal}
              disabled={disabled}
              sx={{
                background: "none",
                border: 0,
                color: disabled ? "text.primary" : "primary.main",
                cursor: disabled ? "default" : "pointer",
                font: "inherit",
                fontSize: 16,
                fontWeight: 500,
                p: 0,
                textDecoration: disabled ? "none" : "underline",
              }}
            >
              {formatWeight(totalOutboundWeight)}
            </Typography>
          </Box>
          {!disabled && (
            <Typography mt={0.75} variant="body2" color="primary.main">
              Click the total to set or clear a manual override.
            </Typography>
          )}
          {itemTotalDiffersFromBillingWeight && (
            <Alert severity="info" sx={{ mt: 2, textAlign: "left" }}>
              Line items currently calculate to {formatWeight(calculatedTotalOutboundWeight)}.
              {hasManualOverride
                ? " The displayed billing total has been manually overridden."
                : " The displayed billing total is using the saved outbound weight."}
            </Alert>
          )}
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 500 }}>Image</TableCell>
              <TableCell align="center" sx={{ fontWeight: 500 }}>Item ID</TableCell>
              <TableCell align="center" sx={{ fontWeight: 500 }}>Description</TableCell>
              <TableCell align="center" sx={{ fontWeight: 500 }}>Quantity Requested</TableCell>
              <TableCell align="center" sx={{ fontWeight: 500 }}>Unit Weight</TableCell>
              <TableCell align="center" sx={{ fontWeight: 500 }}>Outbound Weight</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ inventoryItem, quantity, assetWeight, lineWeight }) => {
              const image = getImage(inventoryItem.asset);

              return (
              <TableRow
                key={inventoryItem.id}
                sx={{
                  "& td": {
                    borderBottomColor: "divider",
                    verticalAlign: "middle",
                    textAlign: "center",
                  },
                }}
              >
                <TableCell sx={{ width: 84 }}>
                  {image ? (
                    <Box
                      component="img"
                      src={image}
                      alt={inventoryItem.asset?.itemId || ""}
                      sx={{
                        width: 48,
                        height: 48,
                        objectFit: "cover",
                        borderRadius: 0,
                        border: theme => `1px solid ${theme.palette.divider}`,
                        backgroundColor: "grey.50",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 0,
                        border: theme => `1px solid ${theme.palette.divider}`,
                        backgroundColor: "grey.50",
                        color: "text.secondary",
                        fontSize: 12,
                      }}
                    >
                      N/A
                    </Box>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setSelectedAssetId(inventoryItem.asset?.id)}
                    sx={{
                      background: "none",
                      border: 0,
                      color: "primary.main",
                      cursor: "pointer",
                      font: "inherit",
                      p: 0,
                      textDecoration: "none",
                    }}
                  >
                    {inventoryItem.asset?.itemId}
                  </Box>
                </TableCell>
                <TableCell align="center">{inventoryItem.asset?.description || "-"}</TableCell>
                <TableCell align="center">{quantity}</TableCell>
                <TableCell align="center">{formatWeight(assetWeight)}</TableCell>
                <TableCell align="center">{formatWeight(lineWeight)}</TableCell>
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      {selectedAssetId && (
        <LocationAssetDetailsModal
          assetId={selectedAssetId}
          onClose={() => setSelectedAssetId(null)}
        />
      )}
      <Dialog
        open={overrideModalOpen}
        onClose={savingManualOutboundWeight ? undefined : closeOverrideModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pt: 3, textAlign: "center" }}>
          <Typography fontSize={20} fontWeight={500} color="text.primary">
            Override Outbound Weight
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <TextField
              label="Manual Outbound Weight"
              fullWidth
              size="small"
              variant="outlined"
              type="number"
              value={draftManualOutboundWeight}
              onChange={(e) => setDraftManualOutboundWeight(e.target.value)}
              slotProps={{
                htmlInput: {
                  min: 0,
                  step: "0.01",
                },
              }}
              helperText="Leave blank to use the calculated total from the line items."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeOverrideModal} disabled={savingManualOutboundWeight}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveOverride}
            disabled={savingManualOutboundWeight}
            startIcon={savingManualOutboundWeight && <CircularProgress size={18} color="inherit" />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
